import type { Conversation } from '@twilio/conversations';

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ArchivedChatsListView,
  type ArchivedChatListRow,
} from './ArchivedChatsListView';
import { useConversationPreview } from '../ChatScreen/useConversationPreview';

const formatArchivedTime = (date: Date | null | undefined) => {
  if (!date) {
    return '';
  }

  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatArchivedDate = (date: Date | null | undefined) => {
  if (!date) {
    return '';
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

type ArchivedConversationListRowProps = {
  conversation: Conversation;
  refreshKey: number;
  onPreviewDateChange: (id: string, date: Date | null | undefined) => void;
};

const getConversationId = (conversation: Conversation) =>
  conversation.uniqueName ?? conversation.sid;

const getArchivedConversationListRow = ({
  conversation,
  previewDate,
  selectedUniqueName,
}: {
  conversation: Conversation;
  previewDate: Date | null | undefined;
  selectedUniqueName?: string;
}): ArchivedChatListRow => {
  const id = getConversationId(conversation);
  const timestamp = previewDate ?? conversation.lastMessage?.dateCreated;

  return {
    id,
    title: conversation.friendlyName ?? 'Trade chat',
    dateLabel: formatArchivedDate(timestamp),
    timeLabel: formatArchivedTime(timestamp),
    selected: id === selectedUniqueName,
  };
};

const ArchivedConversationPreviewAdapter = ({
  conversation,
  refreshKey,
  onPreviewDateChange,
}: ArchivedConversationListRowProps) => {
  const preview = useConversationPreview(conversation, refreshKey);
  const id = getConversationId(conversation);
  const previewDate = preview?.dateCreated;

  useEffect(() => {
    onPreviewDateChange(id, previewDate);
  }, [id, onPreviewDateChange, previewDate]);

  return null;
};

type ArchivedChatsListProps = {
  conversations: Conversation[];
  onPressConversation: (uniqueName: string) => void;
  refreshKey: number;
  selectedUniqueName?: string;
};

const ArchivedChatsList = ({
  conversations,
  onPressConversation,
  refreshKey,
  selectedUniqueName,
}: ArchivedChatsListProps) => {
  const [previewDatesById, setPreviewDatesById] = useState<
    Record<string, Date | null | undefined>
  >({});

  const handlePreviewDateChange = useCallback(
    (id: string, date: Date | null | undefined) => {
      setPreviewDatesById((currentPreviewDatesById) => {
        if (currentPreviewDatesById[id] === date) {
          return currentPreviewDatesById;
        }

        return {
          ...currentPreviewDatesById,
          [id]: date,
        };
      });
    },
    [],
  );

  const rows = useMemo(
    () =>
      conversations.map((conversation) =>
        getArchivedConversationListRow({
          conversation,
          previewDate: previewDatesById[getConversationId(conversation)],
          selectedUniqueName,
        }),
      ),
    [conversations, previewDatesById, selectedUniqueName],
  );

  return (
    <>
      {conversations.map((conversation) => (
        <ArchivedConversationPreviewAdapter
          conversation={conversation}
          key={getConversationId(conversation)}
          onPreviewDateChange={handlePreviewDateChange}
          refreshKey={refreshKey}
        />
      ))}
      <ArchivedChatsListView rows={rows} onPressRow={onPressConversation} />
    </>
  );
};

export { ArchivedChatsList };
